/**
 * WALL CORE DEFINITION (2026-08-18, Matt) — the structured core family for wall types,
 * replacing the old "core = whatever material + thickness you typed into the layer
 * table" model. A wall TYPE now declares WHAT its core is (Wood Studs / Metal Studs /
 * CMU / Concrete) and, for the stud families, WHICH curated size — the core layer's
 * material and (for studs) its thickness are DERIVED from that declaration, never set
 * by hand. CMU/Concrete keep a manually-entered core width (their "size" IS the core
 * thickness). Sizes use ACTUAL widths for nominal names (a 2×6 core is 5½″, never 6″).
 *
 * Materials got simpler at the same time: ONE canonical material per core family
 * ('Wood Stud' — no size, no "+ Batt"), because size lives on the type's core
 * definition now and insulation is a per-wall INSTANCE toggle (`insulation`, wall
 * schema) that draws a batt hatch over the core band in plan/section — it was never
 * really a property of the material.
 *
 * This module is pure data + pure functions (the wallFraming.ts convention) — the
 * wall schema's choice lists, commands/library.ts's conform-on-write, typeNaming.ts's
 * wall recipe, and the type editors' staged-layer preview all read from HERE so the
 * curated family has exactly one home.
 */
import type { AssemblyLayer, MaterialId, ParamValue } from './types'

export interface WallCoreSize {
  value: string
  /** the short label used in dropdowns AND type names ('2×6', '3⅝″') */
  label: string
  actualIn: number
  /** the actual width as display text ('5½″') — dropdown hint for the wood family */
  actualLabel: string
}

export const WALL_CORE_TYPES = [
  { value: 'wood', label: 'Wood Studs' },
  { value: 'metal', label: 'Metal Studs' },
  { value: 'cmu', label: 'CMU' },
  { value: 'concrete', label: 'Concrete' },
] as const
export type WallCoreType = (typeof WALL_CORE_TYPES)[number]['value']

/** sawn-lumber family — nominal names, ACTUAL widths */
export const WOOD_STUD_SIZES: WallCoreSize[] = [
  { value: '2x4', label: '2×4', actualIn: 3.5, actualLabel: '3½″' },
  { value: '2x6', label: '2×6', actualIn: 5.5, actualLabel: '5½″' },
  { value: '2x8', label: '2×8', actualIn: 7.25, actualLabel: '7¼″' },
  { value: '2x10', label: '2×10', actualIn: 9.25, actualLabel: '9¼″' },
  { value: '2x12', label: '2×12', actualIn: 11.25, actualLabel: '11¼″' },
]

/** cold-formed steel family — named BY actual web depth (the industry convention) */
export const METAL_STUD_SIZES: WallCoreSize[] = [
  { value: '2-1/2', label: '2½″', actualIn: 2.5, actualLabel: '2½″' },
  { value: '3-5/8', label: '3⅝″', actualIn: 3.625, actualLabel: '3⅝″' },
  { value: '4', label: '4″', actualIn: 4, actualLabel: '4″' },
  { value: '6', label: '6″', actualIn: 6, actualLabel: '6″' },
  { value: '8', label: '8″', actualIn: 8, actualLabel: '8″' },
]

/**
 * The ONE canonical core material per family — created on demand by the conform pass
 * (commands/library.ts) and seeded by ensureDefaultLibrary. 'Concrete' deliberately
 * reuses the existing footing/stem library material (same name → same record).
 * Categories are the curated classification signals (wallFraming.ts#isWoodCoreWall /
 * isConcreteCoreWall — 'Metal' is checked there as an explicit NON-wood signal, since
 * the name 'Metal Stud' would otherwise match the wood-family \bstud\b regex).
 */
export const WALL_CORE_MATERIALS: Record<WallCoreType, {
  name: string
  color: string
  category: string
  cut?: { pattern: string; fgColor: string; bgColor: string }
}> = {
  wood: { name: 'Wood Stud', color: '#d8c9a3', category: 'Wood' },
  metal: { name: 'Metal Stud', color: '#c8ccd0', category: 'Metal' },
  cmu: {
    name: 'CMU', color: '#c4c7c9', category: 'Concrete',
    cut: { pattern: 'crosshatch', fgColor: '#75797c', bgColor: '#e9eaeb' },
  },
  concrete: {
    name: 'Concrete', color: '#b5b8ba', category: 'Concrete',
    cut: { pattern: 'concrete', fgColor: '#75797c', bgColor: '#e9eaeb' },
  },
}

const coreTypeOf = (params: Record<string, ParamValue>): WallCoreType => {
  const v = String(params.coreType ?? 'wood')
  return (WALL_CORE_TYPES.some((t) => t.value === v) ? v : 'wood') as WallCoreType
}

const sizeOf = (family: WallCoreSize[], value: ParamValue, fallback: string): WallCoreSize =>
  family.find((s) => s.value === String(value)) ?? family.find((s) => s.value === fallback)!

/** the resolved stud-size entry, or null for cmu/concrete (no curated size family) */
export function wallCoreSize(params: Record<string, ParamValue>): WallCoreSize | null {
  const ct = coreTypeOf(params)
  if (ct === 'wood') return sizeOf(WOOD_STUD_SIZES, params.woodStudSize, '2x6')
  if (ct === 'metal') return sizeOf(METAL_STUD_SIZES, params.metalStudSize, '3-5/8')
  return null
}

/** DERIVED core thickness in feet — null for cmu/concrete ("size is set by core width").
 *  Rounded to 4 decimals (0.1 mil): the geometry pipeline's clipper quantum (SCALE
 *  10,000 — bim-geometry/clipper.ts) and the seed library's long-standing thickness
 *  convention (2×6 = 0.4583) — an unrounded 5.5/12 would make every boolean outline
 *  disagree with the stored thickness at the 5th decimal, and would move every
 *  existing seeded wall a hair on migration for no drawn difference. */
export function wallCoreThicknessFt(params: Record<string, ParamValue>): number | null {
  const size = wallCoreSize(params)
  return size ? Math.round((size.actualIn / 12) * 1e4) / 1e4 : null
}

/** the core segment of the auto type name: '2×6 Wood Stud' / '3⅝″ Metal Stud' / 'CMU' / 'Concrete' */
export function wallCoreLabel(params: Record<string, ParamValue>): string {
  const ct = coreTypeOf(params)
  const size = wallCoreSize(params)
  if (ct === 'wood') return `${size!.label} Wood Stud`
  if (ct === 'metal') return `${size!.label} Metal Stud`
  return WALL_CORE_MATERIALS[ct].name
}

/**
 * Conform an assembly's FIRST core layer to the resolved core definition — the pure
 * half of the conform-on-write pass, shared by commands/library.ts (live doc), both
 * type-editor adapters (staged preview), and the migration. Returns the patched
 * layers array, or null when nothing changes. `materialIdByName` resolves a canonical
 * core material name to an id in the caller's material universe (doc or firm payload);
 * returning null keeps the current material (the caller creates-then-retries or lets
 * the kernel conform fix it on save).
 */
export function conformWallCoreLayers(
  params: Record<string, ParamValue>,
  layers: AssemblyLayer[],
  core: { start: number; end: number },
  materialIdByName: (name: string) => string | null,
): AssemblyLayer[] | null {
  if (!layers.length) return null
  const coreIdx = Math.min(Math.max(core.start, 0), layers.length - 1)
  const spec = WALL_CORE_MATERIALS[coreTypeOf(params)]
  const matId = materialIdByName(spec.name)
  const thicknessFt = wallCoreThicknessFt(params)
  const cur = layers[coreIdx]
  const nextMat = (matId ?? cur.materialId) as MaterialId
  const nextT = thicknessFt ?? cur.thicknessFt
  if (cur.materialId === nextMat && Math.abs(cur.thicknessFt - nextT) < 1e-9) return null
  return layers.map((l, i) => (i === coreIdx ? { ...l, materialId: nextMat, thicknessFt: nextT } : l))
}

/**
 * Classify a legacy core material into a core family — the migration's parse of the
 * pre-round world where family lived only in the material's name/category. Metal is
 * checked FIRST ('Metal Stud' contains 'stud'), cmu before concrete.
 */
export function classifyCoreMaterial(name: string, category?: string): WallCoreType | null {
  if (category === 'Metal' || /\bmetal\b/i.test(name)) return 'metal'
  if (category === 'Wood' || /\b(wood|stud|timber|lumber)\b/i.test(name)) return 'wood'
  if (/\b(cmu|masonry|block)\b/i.test(name)) return 'cmu'
  if (category === 'Concrete' || /\bconcrete\b/i.test(name)) return 'concrete'
  return null
}

/** the curated size whose actual width is closest to a legacy layer thickness */
export function nearestCoreSize(family: WallCoreSize[], thicknessFt: number): WallCoreSize {
  let best = family[0]
  for (const s of family) {
    if (Math.abs(s.actualIn / 12 - thicknessFt) < Math.abs(best.actualIn / 12 - thicknessFt)) best = s
  }
  return best
}
